import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  message,
  Row,
} from 'antd';
import { getEstatusDeEjecuciones } from 'api/pae/estatusDeEjecuciones';
import { getPadronesMap } from 'api/recaudacion/utils';
import { InputSearchPro } from 'components/InputSearch';
import Select from 'components/Select';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { onError } from 'utils/handlers';
import padronToString from 'utils/padronesToString';
import { hasAdvanceSearchPermissions, hasPadronPermission } from 'utils/permission/models';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import styled from 'styled-components';
import { getCreditos } from 'api/pae/creditos';
import BusquedaAvanzadaDeCredito from './BusquedaAvanzadaDeCreditos';
import BusquedaEjecuciones from './BusquedaEjecuciones';

const PairInputContainer = styled(Row).attrs({
  gutter: 10,
})`
`;

const FormSection = ({ children }) => (
  <Row gutter={10}>
    {children}
  </Row>
);

FormSection.propTypes = {
  children: PropTypes.node.isRequired,
};

// Regla reutilizable: obliga a capturar ambos extremos del rango
const requirePairedField = (pairedFieldName) => ({ getFieldValue }) => ({
  async validator(_, value) {
    const pairedValue = getFieldValue(pairedFieldName);
    const hasValue = value !== undefined && value !== null && value !== '';
    const hasPaired = pairedValue !== undefined && pairedValue !== null && pairedValue !== '';
    if ((hasValue && !hasPaired) || (!hasValue && hasPaired)) {
      throw new Error('Debe capturar el rango completo (inicial y final).');
    }
  },
});

const PairedTextRange = ({
  nameStart,
  labelStart,
  nameEnd,
  labelEnd,
}) => (
  <>
    <Col xs={24} sm={24} md={6}>
      <Form.Item
        name={nameStart}
        label={labelStart}
        dependencies={[nameEnd]}
        rules={[requirePairedField(nameEnd)]}
      >
        <Input allowClear />
      </Form.Item>
    </Col>
    <Col xs={24} sm={24} md={6}>
      <Form.Item
        name={nameEnd}
        label={labelEnd}
        dependencies={[nameStart]}
        rules={[requirePairedField(nameStart)]}
      >
        <Input allowClear />
      </Form.Item>
    </Col>
  </>
);

PairedTextRange.propTypes = {
  nameStart: PropTypes.string.isRequired,
  labelStart: PropTypes.string.isRequired,
  nameEnd: PropTypes.string.isRequired,
  labelEnd: PropTypes.string.isRequired,
};

const SearchForm = ({
  handleSearchData,
  origenesDeCredito,
  setData,
  setParams,
  loading,
  tipoDePadron,
  setTipoDePadron,
  formSearch,
  padronFound,
  setPadronFound,
  estadosDeCarteraPae,
  setSelectedRowKeys,
}) => {
  const { padrones: _padrones } = useSelector((s) => s.catalogs);
  const padrones = _padrones.filter((p) => hasPadronPermission(p.id) && p.id !== 5
    && p?.usado_en_ejecuciones);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [dataExternoBusqueda, setDataExternoBusqueda] = useState([]);
  const [visibleBusquedaPadronesBusqueda, setVisibleBusquedaPadronesBusqueda] = useState(false);
  const [estatusDeEjecuciones, setEstatusDeEjecuciones] = useState([]);
  const [visibleBusqueda, setVisibleBusqueda] = useState(false);
  const [visibleBusquedaDeCredito, setVisibleBusquedaDeCredito] = useState(false);
  const [creditoFiscal, setCreditoFiscal] = useState([]);

  const origenDeCredito = Form.useWatch('origen_del_credito_id', formSearch);

  const timeoutEjecuciones = useRef();
  const DEBOUNCE_MS = 400;

  useEffect(() => () => clearTimeout(timeoutEjecuciones.current), []);

  const formatPadronesBusqueda = async (_padron) => {
    const nombre_completo = padronToString(_padron, tipoDePadron);
    setPadronFound({ ..._padron, nombre_completo });
    formSearch.setFieldsValue({ object_id_padron: _padron.id });
  };

  const handleSearchBusqueda = async () => {
    try {
      setLoadingSearch(true);
      const { q } = formSearch.getFieldsValue();

      if (!q) {
        message.warn('Ingrese al menos un caracter para buscar.');
        setPadronFound(null);
        return;
      }

      const found = await getPadronesMap[tipoDePadron]({ q });
      const results = found?.results || found;

      if (results?.length === 1) {
        const [first] = results;
        await formatPadronesBusqueda(first);
        formSearch.setFieldValue({
          object_id_padron: first.id,
        });
      } else if (results?.length > 1) {
        setDataExternoBusqueda(found);
        setVisibleBusquedaPadronesBusqueda(true);
        setPadronFound(null);
      } else {
        message.warn('No se encontró ningún padron relacionado.');
        setPadronFound(null);
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoadingSearch(false);
    }
  };

  const resetAndValidateBusqueda = () => {
    setPadronFound([]);
    setDataExternoBusqueda([]);
    setVisibleBusquedaPadronesBusqueda(false);
    formSearch.resetFields();
  };

  const displayTitle = (id) => {
    // Validación de entrada
    if (!id || typeof id !== 'number') {
      return 'Padrón';
    }

    // Búsqueda optimizada con early return
    const padron = padrones.find((p) => p?.id === id);

    // Validación del resultado
    if (padron?.descripcion) {
      return `Búsqueda de ${padron.descripcion}`;
    }

    // Fallback con información adicional
    if (padron) {
      return padron.nombre || padron.titulo || 'Padrón sin descripción';
    }

    return 'Padrón no encontrado';
  };

  const onChangeOrigenDeCreditoBusqueda = async (value) => {
    if (!value) {
      formSearch.setFieldsValue({
        tipo_de_padron: null,
        estatus_de_ejecucion_id: null,
        siguiente_estatus_de_ejecucion_id: null,
      });
      return;
    }

    try {
      setLoadingSearch(true);
      const origen = origenesDeCredito.find((o) => o.id === value);
      const _estatusDeEjecuciones = await getEstatusDeEjecuciones({
        origen_del_credito_id: value,
      });

      formSearch.setFieldsValue({ tipo_de_padron: origen.tipo_de_padron });

      setEstatusDeEjecuciones(_estatusDeEjecuciones);
    } catch (error) {
      onError(error);
    } finally {
      setLoadingSearch(false);
    }
  };

  useEffect(() => {
    if (origenDeCredito) {
      onChangeOrigenDeCreditoBusqueda(origenDeCredito);
    }
  }, [origenDeCredito]);

  const onChangeEstatusDeEjecucionBusqueda = async (value) => {
    if (!value) {
      formSearch.setFieldsValue({ siguiente_estatus_de_ejecucion_id: null });
      return;
    }

    const currentStatus = estatusDeEjecuciones.find((e) => e.id === value);
    const existNextStatus = estatusDeEjecuciones
      .find((e) => e.id === currentStatus.estatus_unico_siguiente_de_ejecucion
        && e.puede_aplicar);

    if (currentStatus && existNextStatus) {
      formSearch.setFieldsValue({
        siguiente_estatus_de_ejecucion_id: currentStatus.estatus_unico_siguiente_de_ejecucion,
      });
    } else {
      formSearch.setFieldsValue({
        siguiente_estatus_de_ejecucion_id: null,
      });
    }
  };

  const handleSearchCreditoFiscal = async () => {
    try {
      const { clave } = await formSearch.validateFields(['clave']);
      if (!clave) return;
      setLoadingSearch(true);
      const response = await getCreditos({
        clave,
        origen_del_credito: formSearch.getFieldValue('origen_del_credito_id'),
      });

      if (!response.length) {
        message.warn('No se encontró ningún crédito fiscal.');
        setCreditoFiscal([]);
        formSearch.setFieldsValue({
          credito_fiscal: null,
        });
        return;
      }

      setCreditoFiscal(response);
      formSearch.setFieldsValue({
        credito_fiscal: response[0].id,
      });
    } catch (err) {
      onError(err);
    } finally {
      setLoadingSearch(false);
    }
  };

  const resetAndValidate = () => {
    formSearch.resetFields(['clave', 'credito_fiscal']);
    setCreditoFiscal([]);
  };

  const onFinish = async (params) => {
    try {
      clearTimeout(timeoutEjecuciones.current);
      timeoutEjecuciones.current = setTimeout(async () => {
        try {
          setLoadingSearch(true);
          const { folio_de_ejecucion } = formSearch.getFieldsValue();

          await handleSearchData({ folio_de_ejecucion, ...params });
        } catch (error) {
          onError(error);
        } finally {
          setLoadingSearch(false);
        }
      }, DEBOUNCE_MS);
    } catch (error) {
      onError(error);
    }
  };

  return (
    <Form
      layout="vertical"
      form={formSearch}
      scrollToFirstError
      onFinish={onFinish}
      className="without-feedback"
    >
      <Row gutter={10}>
        <Col xs={24} md={8}>
          <Form.Item name="origen_del_credito_id" label="Origen de crédito">
            <Select
              dataSource={origenesDeCredito.filter((e) => e.estados_globales !== 5)}
              onChange={onChangeOrigenDeCreditoBusqueda}
              disabled={loadingSearch}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="estatus_de_ejecucion_id" label="Estatus actual de reporte moroso">
            <Select
              dataSource={origenDeCredito ? estatusDeEjecuciones
                .filter((e) => e.es_estatus_unico_inicial || !e.solo_uso_interno)
                .filter((e) => e.puede_aplicar) : []}
              disabled={loadingSearch}
              onChange={onChangeEstatusDeEjecucionBusqueda}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="siguiente_estatus_de_ejecucion_id" label="Siguiente estatus de reporte moroso">
            <Select
              dataSource={(origenDeCredito ? estatusDeEjecuciones
                .filter((e) => e.id !== 1
                && e.solo_uso_interno === false && e.puede_aplicar) : [])}
              disabled={loadingSearch}
              allowClear
            />
          </Form.Item>
        </Col>
        <InputSearchPro
          disabled={loadingSearch}
          prefix={{
            style: { width: '100%' },
            name: 'tipo_de_padron',
            label: 'padron',
            dataSource: padrones,
            onChange: setTipoDePadron,
            valueProp: 'content_type',
          }}
          name="q"
          onClickAdvanced={hasAdvanceSearchPermissions(tipoDePadron)
            ? () => setVisibleBusquedaPadronesBusqueda(true) : null}
          label={displayTitle(tipoDePadron)}
          inputProps={{
            onPressEnter: handleSearchBusqueda,
          }}
          breakPoints={{
            xs: 24,
            sm: 24,
            md: 12,
          }}
          resultProps={{
            breakPoints: {
              xs: 24,
              sm: 24,
              md: 12,
            },
            name: 'object_id_padron',
            labelProp: 'nombre_completo',
            label: displayTitle(tipoDePadron),
            dataSource: padronFound,
            onClear: resetAndValidateBusqueda,
          }}
        />
        <InputSearchPro
          disabled={loadingSearch}
          tooltip="Búsqueda de crédito fiscal por clave"
          name="clave"
          label="Crédito fiscal"
          inputProps={{
            onPressEnter: () => handleSearchCreditoFiscal(),
          }}
          onClickAdvanced={() => setVisibleBusquedaDeCredito(true)}
          breakPoints={{
            xs: 24,
            sm: 24,
            md: 12,
          }}
          resultProps={{
            breakPoints: {
              xs: 24,
              sm: 24,
              md: 12,
            },
            name: 'credito_fiscal',
            render: (value) => `${value.clave} - ${value?.numero_de_cuenta_del_credito || 'Sin número de cuenta'}`,
            label: 'Crédito fiscal',
            dataSource: creditoFiscal,
            onClear: resetAndValidate,
          }}
        />
      </Row>
      <Collapse>
        <Collapse.Panel header="Más filtros" key="1">
          <PairInputContainer>
            <PairedTextRange
              nameStart="folio_inicial_de_ejecucion"
              labelStart="Folio inicial de reporte moroso"
              nameEnd="folio_final_de_ejecucion"
              labelEnd="Folio final de reporte moroso"
            />
            <PairedTextRange
              nameStart="folio_id_inicial_de_ejecucion"
              labelStart="Folio ID inicial de reporte moroso"
              nameEnd="folio_id_final_de_ejecucion"
              labelEnd="Folio ID final de reporte moroso"
            />
            <PairedTextRange
              nameStart="folio_id_inicial_de_generacion"
              labelStart="Folio ID inicial de generación de reporte moroso"
              nameEnd="folio_id_final_de_generacion"
              labelEnd="Folio ID final de generación de reporte moroso"
            />
            <Col xs={24} md={6}>
              <Form.Item name="solo_con_estado_de_cartera_ejecutada_id" label="Solo con estatus de cartera ejecutada">
                <Select dataSource={estadosDeCarteraPae} disabled={loadingSearch} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="solo_con_estado_de_gastos_de_ejecucion_id" label="Solo con estatus de gastos de reporte moroso">
                <Select dataSource={estadosDeCarteraPae} disabled={loadingSearch} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="rango_de_fecha_de_ejecucion"
                label="Rango de fecha de reporte moroso"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || value === null) return Promise.resolve();
                      const isValid = Array.isArray(value)
                        && value.length === 2
                        && value[0]
                        && value[1];
                      return isValid
                        ? Promise.resolve()
                        : Promise.reject(new Error('Seleccione fecha inicial y final.'));
                    },
                  },
                ]}
              >
                <DatePicker.RangePicker
                  disabled={loading}
                  format="DD/MM/YYYY"
                  placeholder={['', '']}
                />
              </Form.Item>
            </Col>
          </PairInputContainer>
        </Collapse.Panel>
      </Collapse>
      <br />
      <Form.Item hidden>
        <Button type="primary" htmlType="submit" />
      </Form.Item>
      <Row gutter={[10, 10]}>
        <Col xs={24} md={8}>
          <Button
            htmlType="button"
            onClick={() => {
              resetAndValidateBusqueda();
              setData([]);
              setParams({});
              setSelectedRowKeys([]);
              setCreditoFiscal([]);
              formSearch.resetFields();
            }}
            icon={<DeleteOutlined />}
            block
          >
            Limpiar
          </Button>
        </Col>
        <Col xs={24} md={8}>
          <Button htmlType="submit" loading={loading} icon={<SearchOutlined />} block>
            Buscar
          </Button>
        </Col>
      </Row>
      <br />
      <BusquedaAvanzadaPadrones
        visible={visibleBusquedaPadronesBusqueda}
        setVisible={setVisibleBusquedaPadronesBusqueda}
        callback={formatPadronesBusqueda}
        dataExterno={dataExternoBusqueda}
        setDataExterno={setDataExternoBusqueda}
        tipoDePadron={tipoDePadron}
      />
      <BusquedaEjecuciones
        visible={visibleBusqueda}
        setVisible={setVisibleBusqueda}
        onFinishBusqueda={(params) => {
          setVisibleBusqueda(false);
          setParams(params);
          onFinish(params);
        }}
      />
      <BusquedaAvanzadaDeCredito
        visible={visibleBusquedaDeCredito}
        setVisible={setVisibleBusquedaDeCredito}
        origenDeCredito={formSearch.getFieldValue('origen_del_credito_id')}
        callback={(credito) => {
          setCreditoFiscal([credito]);
          formSearch.setFieldsValue({
            credito_fiscal: credito.id,
          });
        }}
      />
    </Form>
  );
};

SearchForm.propTypes = {
  handleSearchData: PropTypes.func.isRequired,
  origenesDeCredito: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    descripcion: PropTypes.string,
  })).isRequired,
  setData: PropTypes.func.isRequired,
  setParams: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  estadosDeCarteraPae: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    descripcion: PropTypes.string,
  })).isRequired,
  tipoDePadron: PropTypes.number.isRequired,
  setTipoDePadron: PropTypes.func.isRequired,
  formSearch: PropTypes.shape({
    getFieldsValue: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    resetFields: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
    validateFields: PropTypes.func.isRequired,
  }).isRequired,
  padronFound: PropTypes.shape({
    id: PropTypes.number,
    nombre_completo: PropTypes.string,
  }).isRequired,
  setPadronFound: PropTypes.func.isRequired,
  setSelectedRowKeys: PropTypes.func.isRequired,
};

export default SearchForm;
